import lodash from 'lodash';
import { serialize, deserialize } from './serialization.js';
import { ModelIdSymbol } from '../constants.js';
import { createIsInstance, omitNullable } from '../utils.js';
import { isNumberUnit } from '@exodus/currency';
import { safeFields } from './utils.js';
const { isEqual, pick, mapValues, isString, cloneDeepWith } = lodash;
export * from './serialization.js';
function assetUnitsToRegex(token) {
    const units = Object.keys(token.currency.units);
    return new RegExp(` (${units.join('|')})$`);
}
export default class AccountState {
    static defaults = Object.create(null);
    static _allFieldNames = null;
    static _fieldNames = null;
    static _version = 0;
    static _tokens = [];
    static _dateKeys = [];
    static get [ModelIdSymbol]() {
        return 'AccountState';
    }
    static _isInstance = createIsInstance(AccountState);
    static [Symbol.hasInstance](x) {
        return this._isInstance(x);
    }
    static isInstance = AccountState[Symbol.hasInstance];
    static _getAllFieldNames() {
        if (!this._allFieldNames) {
            this._allFieldNames = Object.keys(this.defaults);
            this._allFieldNames.push('_version');
        }
        return this._allFieldNames;
    }
    static _getFieldNames() {
        const fields = this._getAllFieldNames();
        if (!this._fieldNames) {
            this._fieldNames = fields.filter((name) => name !== 'mem');
        }
        return this._fieldNames;
    }
    static serialize(json, { includeMem = false } = Object.create(null)) {
        const keys = includeMem ? this._getAllFieldNames() : this._getFieldNames();
        return serialize(pick(json, keys));
    }
    static deserialize(serialized) {
        const json = serialized.v && serialized.v._version === 1
            ? deserialize(serialized)
            : this._legacyParse(serialized);
        return new this(json);
    }
    static fromJSON(serialized) {
        return this.deserialize(serialized);
    }
    static create(props) {
        return new this(props);
    }
    _version;
    constructor(props = Object.create(null)) {
        const { defaults } = this.constructor;
        if (this.constructor === AccountState) {
            throw new Error('Instantiating the base AccountState class is forbidden, please instantiate a subclass instance');
        }
        const allProps = {
            ...defaults,
            ...pick(props, this.constructor._getAllFieldNames()),
        };
        Object.assign(this, allProps, { _version: 1 });
    }
    static _legacyParse(rawData) {
        return this._postParse(mapValues(rawData, (v, k) => {
            if (this._dateKeys.includes(k) && v) {
                return new Date(v);
            }
            const assetFound = isString(v) && this._tokens.find((token) => assetUnitsToRegex(token).test(v));
            if (assetFound) {
                return assetFound.currency.parse(v);
            }
            return v;
        }));
    }
    static _postParse(data) {
        return data;
    }
    toJSON(opts) {
        return this.constructor.serialize(this, opts);
    }
    toRedactedJSON() {
        const obj = { ...this };
        const cloned = cloneDeepWith(obj, (value, key) => {
            if (value === obj)
                return;
            if (typeof value === 'number' || typeof value === 'boolean')
                return value;
            if (safeFields.has(key))
                return;
            if (isNumberUnit(value))
                return value.toDefaultString({ unit: true });
            return null;
        });
        return omitNullable(cloned);
    }
    equals(other, opts = { includeMem: true }) {
        return this === other || isEqual(this.toJSON(opts), other.toJSON(opts));
    }
    merge(data) {
        return this.constructor.create({ ...this, ...data });
    }
    contains(data) {
        return this.equals(this.merge(data));
    }
}
